import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Menu } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import MobileMenu from "./MobileMenu";
import BrandLogo from "./BrandLogo";
import SocialIcons from "./SocialIcons";

export default function Navbar({ transparent = false }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  const isOverlay = transparent && !scrolled;
  const headerClass = isOverlay ? "transparent-header" : "glass-header";

  const links = [
    { to: "/", label: "Home", end: true },
    { to: "/tours", label: t("nav.tours") || "Tours" },
    { to: "/blog", label: "Blog" },
    { to: "/about", label: "About" },
    { to: "/consultations", label: "Consultation" },
    { to: "/partner", label: "Partnership" },
  ];

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 ${headerClass} transition-all duration-500`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div
            className={
              isOverlay
                ? "nav-adaptive flex items-center gap-10 flex-1"
                : "flex items-center gap-10 flex-1"
            }
          >
            <BrandLogo
              variant={isOverlay ? "light" : "dark"}
              size="md"
              testid="nav-logo"
            />

            <nav className="hidden md:flex items-center gap-8 ml-10">
              {links.map((l, i) => (
                <NavLink
                  key={i}
                  to={l.to}
                  end={l.end}
                  data-testid={`nav-link-${i}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-opacity ${
                      isActive
                        ? "font-semibold"
                        : "opacity-90 hover:opacity-100"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Desktop right side */}
          <div className="hidden md:flex items-center gap-3">
            {/* Social icons — small, between nav and pills */}
            <div className={isOverlay ? "nav-adaptive hidden lg:block" : "hidden lg:block"}>
              <SocialIcons
                variant={isOverlay ? "light" : "dark"}
                size={14}
                compact
                testidPrefix="nav-social"
                items={["whatsapp", "instagram", "facebook"]}
              />
            </div>
            <div className={isOverlay ? "nav-adaptive h-5 w-px bg-current/30 hidden lg:block" : "h-5 w-px bg-slate-300 hidden lg:block"} />

            <div
              className={
                isOverlay
                  ? "nav-adaptive flex items-center gap-2"
                  : "flex items-center gap-2"
              }
            >
              <LanguageSwitcher />
              {user && <NotificationBell />}
              {user && user.role === "admin" && (
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className="text-sm px-2 opacity-90 hover:opacity-100"
                >
                  {t("nav.admin")}
                </NavLink>
              )}
              {user && (
                <NavLink
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="text-sm px-2 opacity-90 hover:opacity-100"
                >
                  {t("nav.dashboard")}
                </NavLink>
              )}
            </div>

            {user ? (
              <button
                data-testid="nav-logout"
                onClick={onLogout}
                className="btn-pill bg-slate-900 text-white hover:bg-slate-800 text-sm shadow-xl"
              >
                {t("nav.logout")}
              </button>
            ) : (
              <NavLink
                to="/login"
                data-testid="nav-login"
                className="btn-pill bg-slate-900 text-white hover:bg-slate-800 text-sm shadow-xl"
              >
                Sign In
              </NavLink>
            )}
          </div>

          {/* Mobile: social icons (compact) + burger */}
          <div className="md:hidden flex items-center gap-1">
            <div className={isOverlay ? "nav-adaptive" : ""}>
              <SocialIcons
                variant={isOverlay ? "light" : "dark"}
                size={13}
                compact
                items={["whatsapp", "instagram"]}
                testidPrefix="nav-social-m"
                className="!gap-1.5"
              />
            </div>
            <button
              onClick={() => setOpen(true)}
              className={`p-2 ml-1 ${isOverlay ? "nav-adaptive" : "text-slate-900"}`}
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <MobileMenu
        open={open}
        onClose={() => setOpen(false)}
        user={user}
        onLogout={onLogout}
      />
    </>
  );
}
